import React from 'react';
import { Link } from 'react-router-dom';
import { getModuleById } from '@/data/moduleData';
import { ArrowLeft, ArrowRight, Clock, BookOpen } from 'lucide-react';

const ModuleStub = ({ moduleId }) => {
  const module = getModuleById(moduleId);
  if (!module) return null;

  const prevIndex = ['foundation', 'transformers', 'per-unit', 'symmetrical-components', 'sequence-networks', 'fault-analysis', 'protection', 'distribution-design'].indexOf(moduleId);
  const allIds = ['foundation', 'transformers', 'per-unit', 'symmetrical-components', 'sequence-networks', 'fault-analysis', 'protection', 'distribution-design'];

  return (
    <div data-testid={`module-stub-${moduleId}`} className="min-h-[60vh]">
      {/* Module header */}
      <div className="bg-gradient-to-br from-[#0B172A] to-slate-800 text-white px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Curriculum Map
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-mono font-semibold text-teal-400 bg-teal-400/10 border border-teal-400/20 px-2 py-1 rounded">
              {module.number}
            </span>
            <span className="text-xs text-slate-400 uppercase tracking-widest">Module</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3">{module.title}</h1>
          <p className="text-slate-300 text-lg mb-4">{module.subtitle}</p>
          <p className="text-slate-400 max-w-2xl">{module.description}</p>
        </div>
      </div>

      {/* Coming Soon content */}
      <div className="px-4 sm:px-6 lg:px-8 py-16 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 mb-6">
          <Clock className="h-7 w-7 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-2xl font-semibold font-heading text-slate-900 dark:text-white mb-3">
          Coming in Phase 2
        </h2>
        <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-2 leading-relaxed">
          This module is planned and fully scoped. It will include theory, formula cards, a worked example,
          interactive calculators, and a self-check quiz — following the same format as the live Per-Unit Analysis module.
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-500 mb-8">
          In the meantime, start with the fully built reference module below.
        </p>

        {/* What's coming preview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 text-left">
          {[
            { icon: BookOpen, title: 'Theory & Formulas', desc: 'Core definitions, derivations, and KaTeX formula cards' },
            { icon: BookOpen, title: 'Worked Example', desc: 'Realistic utility scenario solved step-by-step' },
            { icon: BookOpen, title: 'Interactive Calculator', desc: 'Hands-on calculator with teaching tooltips' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="w-8 h-8 rounded-md bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mb-3">
                <Icon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              </div>
              <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">{title}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
            </div>
          ))}
        </div>

        <Link
          to="/per-unit"
          data-testid="stub-goto-per-unit"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-500 text-white font-medium rounded-md transition-colors"
        >
          Open Per-Unit Analysis (Live)
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

export default ModuleStub;
